import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { FileSignature, FileText, CheckCircle2, Eraser, Clock } from "lucide-react";

function SignaturePad({ canvasRef }) {
  const drawing = useRef(false);
  const last = useRef(null);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: cx - r.left, y: cy - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.strokeStyle = "#0b2a5b"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; };

  return (
    <canvas ref={canvasRef} width={440} height={150} data-testid="signature-canvas"
      className="w-full border-2 border-dashed border-nardo-300 rounded-lg bg-white touch-none cursor-crosshair"
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
  );
}

export default function ContractSign() {
  const [contracts, setContracts] = useState([]);
  const [signFor, setSignFor] = useState(null);
  const [signer, setSigner] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);

  const load = () => api.get("/contract/mine").then((r) => setContracts(r.data || [])).catch(() => setContracts([]));
  useEffect(() => { load(); }, []);

  const clearPad = () => {
    const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };
  const isBlank = () => {
    const c = canvasRef.current; if (!c) return true;
    const blank = document.createElement("canvas"); blank.width = c.width; blank.height = c.height;
    return c.toDataURL() === blank.toDataURL();
  };

  const download = async (c) => {
    try {
      const res = await api.get(`/contract/${c.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Contrato_ZAVIA_${(c.colegio || "colegio").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar"); }
  };

  const submitSign = async () => {
    if (!signer.trim()) { toast.error("Escribe tu nombre completo"); return; }
    if (!accepted) { toast.error("Debes aceptar los términos del contrato"); return; }
    if (isBlank()) { toast.error("Dibuja tu firma en el recuadro"); return; }
    setSaving(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      await api.post(`/contract/${signFor.id}/sign`, { signature, signer: signer.trim() });
      toast.success("¡Contrato firmado! Ya puedes descargarlo.");
      setSignFor(null); setSigner(""); setAccepted(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo firmar"); }
    finally { setSaving(false); }
  };

  if (contracts.length === 0) return null;

  return (
    <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 mt-6" data-testid="contract-sign-card">
      <h3 className="font-display font-bold text-navy text-lg flex items-center gap-2"><FileSignature className="w-5 h-5" /> Contrato del servicio</h3>
      <p className="text-sm text-nardo-500 mt-1 mb-4">Revisa y firma tu contrato en línea, sin imprimir. Una vez firmado podrás descargarlo cuando quieras.</p>
      <div className="space-y-2">
        {contracts.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 border border-nardo-100 rounded-lg px-4 py-3" data-testid={`my-contract-${c.id}`}>
            <div className="min-w-0">
              <div className="text-navy font-medium text-sm">{c.plan_name} · {c.colegio}</div>
              <div className="text-[11px] text-nardo-400">Emitido {new Date(c.created_at).toLocaleDateString("es-MX")} · Vigencia 1 año</div>
            </div>
            <div className="flex items-center gap-2">
              {c.status === "firmado" ? (
                <span className="text-[11px] inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"><CheckCircle2 className="w-3.5 h-3.5" /> Firmado</span>
              ) : (
                <span className="text-[11px] inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1"><Clock className="w-3.5 h-3.5" /> Pendiente de firma</span>
              )}
              {c.status !== "firmado" && (
                <button onClick={() => { setSignFor(c); setSigner(c.responsable || ""); setAccepted(false); setTimeout(clearPad, 100); }} data-testid={`sign-open-${c.id}`}
                  className="text-[12px] bg-navy hover:bg-navy-light text-white rounded-md px-3 py-1.5 inline-flex items-center gap-1 font-medium">
                  <FileSignature className="w-3.5 h-3.5" /> Revisar y firmar
                </button>
              )}
              <button onClick={() => download(c)} data-testid={`my-contract-download-${c.id}`}
                className="text-[12px] text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-md px-3 py-1.5 inline-flex items-center gap-1 transition-colors">
                <FileText className="w-3.5 h-3.5" /> PDF
              </button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!signFor} onOpenChange={(o) => { if (!o) setSignFor(null); }}>
        <AlertDialogContent data-testid="sign-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Firmar contrato en línea</AlertDialogTitle>
            <AlertDialogDescription>
              {signFor?.plan_name} · {signFor?.colegio}. Revisa el PDF, dibuja tu firma y acepta los términos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-1">
            <button onClick={() => signFor && download(signFor)} data-testid="sign-review-pdf" className="text-[12px] text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-md px-3 py-1.5 inline-flex items-center gap-1 transition-colors">
              <FileText className="w-3.5 h-3.5" /> Ver contrato (PDF)
            </button>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Nombre de quien firma</label>
              <input value={signer} onChange={(e) => setSigner(e.target.value)} data-testid="sign-name" className="w-full mt-1 border border-nardo-200 rounded-md px-3 py-2 text-sm" placeholder="Nombre completo del responsable" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Tu firma</label>
                <button onClick={clearPad} data-testid="sign-clear" className="text-[11px] text-nardo-500 hover:text-navy inline-flex items-center gap-1"><Eraser className="w-3 h-3" /> Borrar</button>
              </div>
              <SignaturePad canvasRef={canvasRef} />
            </div>
            <label className="flex items-start gap-2 text-xs text-navy cursor-pointer">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="sign-accept" className="mt-0.5 w-4 h-4 accent-navy" />
              <span>He leído y <b>acepto los términos y condiciones</b> del contrato de prestación de servicios con ZAVIA.</span>
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sign-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="sign-submit" onClick={(e) => { e.preventDefault(); submitSign(); }} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? "Firmando…" : "Firmar y aceptar"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
